/**
 * The OAuth consent screen an MCP client sends the user to.
 *
 * The Worker has already refused an unknown client or an unregistered return
 * address before this page is served — those are dead ends, server-rendered,
 * because redirecting an error to an unverified address is how an
 * authorization server becomes an open redirector. So everything here is a
 * decision about a client that checks out.
 *
 * The decision is a real form POST to `/oauth/authorize`, echoing every
 * authorize parameter back as a hidden field. The Worker re-validates all of
 * them rather than trusting them — they came from a browser — and answers with
 * the 302 that carries the code back to the client, which only a navigation
 * can follow.
 */

import type { ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { Skeleton, SkeletonRows } from "../components/ui";
import { useAsync } from "../useAsync";

/**
 * Every parameter `GET /oauth/authorize` reads, echoed back through the POST.
 * Mirrors `AUTHORIZE_PARAMS` in src/authserver/routes.ts — the server reads
 * these from the form and re-checks them, so a missing one here is a request
 * that fails at the decision rather than at the start.
 */
const AUTHORIZE_PARAMS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "code_challenge",
  "code_challenge_method",
  "state",
  "scope",
  "resource",
] as const;

const GRANTS: Array<{ title: string; body: string }> = [
  {
    title: "Read the browser tab you are looking at",
    body: "On request: the page's structure, its text, a screenshot, the list of open tabs, and the values a feature's extractors read. Only when the assistant asks, and only from a browser you have linked.",
  },
  {
    // Driving is a new capability, not a shade of "read": it acts in the user's
    // logged-in session. It is named here because the consent screen is the
    // only place they are asked, and a grant nobody was shown is not a grant.
    title: "Act in that tab, as you",
    body: "On request: click, type, navigate, scroll, and change what a page shows — in your own logged-in session. A fixed set of operations the extension checks before performing, each one recorded in the Activity tab. Running JavaScript is separate: the extension's own switch for it is on by default, but nothing can run until you also allow user scripts for the extension in Chrome, and you can turn it off in Settings at any time.",
  },
  {
    title: "Change this extension's configuration",
    body: "Add, edit, preview, disable and delete features, and roll the configuration back. Every version is kept; you can restore any of them yourself.",
  },
  {
    title: "Upload and run logic modules",
    body: "Small sandboxed WebAssembly programs, run in your browser with no imports — everything they touch is an effect the extension checks first.",
  },
];

export function Authorize(): ReactNode {
  const [params] = useSearchParams();
  const consent = useAsync(() => api.consent(window.location.search), [window.location.search]);

  return (
    <Container style={{ maxWidth: "38rem" }}>
      <Card>
        <Card.Body className="p-4 p-md-5">
          <p className="emb-eyebrow mb-2">Authorise</p>

          {consent.loading ? (
            <>
              <Skeleton width="70%" height="1.8rem" className="mb-3" />
              <SkeletonRows rows={5} />
            </>
          ) : consent.error ? (
            <Alert variant="danger" className="mb-0">
              <p className="fw-semibold mb-1">This request cannot be shown.</p>
              <p className="mb-0 small">{consent.error} Nothing was authorised. Start the connection again from your assistant.</p>
            </Alert>
          ) : consent.data ? (
            <Form method="post" action="/oauth/authorize">
              {AUTHORIZE_PARAMS.map((name) => {
                const value = params.get(name);
                return value === null ? null : <input key={name} type="hidden" name={name} value={value} />;
              })}

              <h1 className="h4 mb-3">
                Let <strong>{consent.data.clientName}</strong> use your account?
              </h1>

              <div className="emb-sunken p-3 mb-4">
                <div className="small emb-muted">Signed in as</div>
                <div className="fw-semibold">{consent.data.account.username}</div>
                <div className="emb-muted small">
                  {consent.data.account.email ?? "no verified email"} · {consent.data.account.provider}
                </div>
              </div>

              <p className="fw-semibold mb-2">If you approve, it will be able to:</p>
              <ul className="list-unstyled d-flex flex-column gap-3 mb-4">
                {GRANTS.map((grant) => (
                  <li key={grant.title}>
                    <div className="fw-semibold small">{grant.title}</div>
                    <div className="emb-muted small">{grant.body}</div>
                  </li>
                ))}
              </ul>

              <div className="emb-accent-panel p-3 mb-4">
                <p className="small mb-0">
                  This lasts until you revoke it on the <a href="/account">Account</a> page — revoking takes effect on
                  the connection's next call. It cannot read your captured data: capture logs live in your extension and
                  are never sent here. Every call it makes is recorded and shown live in the extension's Activity view.
                </p>
              </div>

              <p className="emb-muted small">
                After approving you will be returned to <code className="emb-mono">{consent.data.redirectHost}</code>
                {consent.data.clientUri ? (
                  <>
                    {" "}
                    (<a href={consent.data.clientUri} target="_blank" rel="noreferrer noopener">
                      {consent.data.clientUri}
                    </a>
                    )
                  </>
                ) : null}
                .
              </p>

              <div className="d-flex gap-2">
                <Button type="submit" name="decision" value="approve" variant="primary" className="flex-grow-1">
                  Approve
                </Button>
                <Button type="submit" name="decision" value="deny" variant="light">
                  Cancel
                </Button>
              </div>
            </Form>
          ) : null}
        </Card.Body>
      </Card>
    </Container>
  );
}
